'use client';
import LinkIconSvg from '@/assets/svgs/text-editor/link-01.svg';
import classNames from 'classnames';

import { getShortcutModifier, isMac } from '@/shared/utils/window';
import { useState } from 'react';
import useTextEditorShortcuts from '../hooks/useTextEditorShortcuts';
import KeyboardShortcutText from './KeyboardShortcutText';
import LinksDropdown from './LinksDropdown';

interface InsertLinkDropdownButtonProps {
  className?: string;
  isInlineToolbar?: boolean;
  isIconButton?: boolean;
  jobId?: string;
}

const InsertLinkDropdownButton = ({
  className,
  isInlineToolbar,
  isIconButton,
  jobId = '',
}: InsertLinkDropdownButtonProps) => {
  const { closeFloatingItems } = useTextEditorShortcuts(jobId);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [isClickOpen, setIsClickOpen] = useState(false);

  const handleHoverEnter = () => {
    setIsHoverOpen(true);
    setIsClickOpen(false); // Close click dropdown when hover opens
  };

  const handleHoverLeave = () => {
    setIsHoverOpen(false);
  };

  const handleClickToggle = () => {
    closeFloatingItems();
    setIsClickOpen((prev) => !prev);
    setIsHoverOpen(false); // Close hover dropdown when click toggles
  };

  return (
    <div
      className={classNames(
        'dropdown',
        {
          'dropdown-top dropdown-hover': isHoverOpen && !isClickOpen,
          'dropdown-end dropdown-left': isInlineToolbar,
        },
        className
      )}
      data-dropdown-dismiss="true">
      <div
        tabIndex={0}
        role="button"
        onMouseDown={(e) => {
          e.preventDefault(); // Prevent button from stealing focus from editor
        }}
        onClick={handleClickToggle}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
        data-button-id="editor-toolbar-insert-link-button"
        className={classNames('btn btn-sm', {
          'btn-square btn-ghost': isIconButton,
          'btn-primary btn-block px-2': !isIconButton,
        })}>
        {isIconButton ? <LinkIconSvg /> : 'Insert link'}
      </div>
      {isHoverOpen && !isClickOpen && !isInlineToolbar && (
        <div
          tabIndex={0}
          className="dropdown-content bg-black-default/80 z-[1] mb-0.5 !flex h-[26px] min-w-[148px] items-center justify-center gap-1.5 rounded-md px-[8px] text-white">
          <div className="text-[12px]">Insert Link</div>
          <div className="shortcuts flex gap-1">
            <KeyboardShortcutText
              shortcut={getShortcutModifier()}
              className="text-[16px]"
            />
            <KeyboardShortcutText
              shortcut="⇧"
              className={isMac() ? 'pb-0.5 text-[16px]' : 'text-[12px]'}
            />
            <KeyboardShortcutText shortcut="L" className="text-[12px]" />
          </div>
        </div>
      )}
      {isClickOpen && (
        <LinksDropdown onClose={() => setIsClickOpen(false)} jobId={jobId} />
      )}
    </div>
  );
};

export default InsertLinkDropdownButton;
